'use client';

import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { formatPrice } from '@/lib/utils';
import type { MidTermAvailability } from '@/server/services/course.service';

export type MidTermBadgeProps = {
  midTermAvailability: MidTermAvailability;
  showPrice?: boolean;
};

export function MidTermBadge({ midTermAvailability, showPrice = true }: MidTermBadgeProps) {
  const t = useTranslations('public.courseCard');
  const { availableSeats, proratedPriceCents, remainingSessions, state } = midTermAvailability;

  // A class that is FULL. Truthful rather than encouraging: the request this
  // leads to is one an admin can waitlist, which is exactly what the word says.
  if (state === 'waitlist') {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge className="bg-amber-100 hover:bg-amber-100 text-amber-800 border border-amber-300 text-xs font-semibold">
          {t('waitlist')}
        </Badge>
        <span className="text-xs font-medium text-muted-foreground">{t('classIsFull')}</span>
      </div>
    );
  }

  // A class that is ENDING, not oversubscribed. Deliberately NOT a waitlist:
  // implying a seat might open in a class that is finishing sends the parent to
  // wait for something that cannot happen.
  if (state === 'ending') {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="text-xs font-semibold">
          {t('endsSoon')}
        </Badge>
        <span className="text-xs font-medium text-muted-foreground">
          {t('sessionsLeft', { count: remainingSessions })}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge className="bg-[#2fa5fb] hover:bg-[#2fa5fb]/90 text-white text-xs font-semibold">
        {t('joinNowMidTerm')}
      </Badge>
      <span className="text-xs font-medium text-[#22c55e]">
        {t('spotsOpen', { count: availableSeats })}
      </span>
      {showPrice && (
        <span className="text-xs font-medium text-muted-foreground">
          {t('fromPrice', { price: formatPrice(proratedPriceCents) })}
        </span>
      )}
    </div>
  );
}
